/**
 * BigQueryのDATE型やDateオブジェクトをYYYY-MM-DD文字列に変換
 * BigQueryからの日付は { value: "2026-01-12" } 形式で返ることがある
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business BQのDATE型（{ value: "..." }形式）やJSのDateオブジェクトをYYYY-MM-DD文字列に正規化
 * @graph-connects none
 */
export function extractDateString(date: unknown): string {
  if (typeof date === 'string') {
    return date;
  }
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }
  if (date && typeof date === 'object' && 'value' in date) {
    return String((date as { value: unknown }).value);
  }
  if (date === null || date === undefined) {
    return '';
  }
  // 未知の型の場合は空文字を返す
  return '';
}

/**
 * 日付（ISO文字列 / Date / BQ {value} 形式）を YYYY/MM/DD 形式に整形する。
 * 時刻部分は落とす。抽出できない場合は空文字を返す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 日付を YYYY/MM/DD 表示形式に整形（時刻を除去し区切りをスラッシュに統一）
 * @graph-connects extractDateString [calls] 日付文字列抽出関数呼び出し
 */
export function formatYmdSlash(date: unknown): string {
  const extracted = extractDateString(date);
  if (!extracted) return '';
  return extracted.slice(0, 10).replace(/-/g, '/');
}

/**
 * オブジェクト内のdate/Date関連フィールドを文字列に変換
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business オブジェクト内のdate/Dateキーを含むフィールドを一括でYYYY-MM-DD文字列に変換
 * @graph-connects extractDateString [calls] 日付文字列抽出関数呼び出し
 */
export function normalizeDates<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  const result = { ...obj } as Record<string, unknown>;
  for (const key of Object.keys(result)) {
    const value = result[key];
    // date/Dateを含むキー名のフィールドを変換
    if (key.toLowerCase().includes('date') && value !== null && value !== undefined) {
      result[key] = extractDateString(value);
    }
  }
  return result as T;
}

/**
 * 配列内の全オブジェクトの日付フィールドを正規化
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 配列内の全オブジェクトに対してnormalizeDatesを適用し、日付フィールドを一括正規化
 * @graph-connects normalizeDates [calls] 個別オブジェクトの日付正規化関数呼び出し
 */
export function normalizeDateArray<T>(arr: T[]): T[] {
  return arr.map(normalizeDates);
}

/**
 * 日時の要素を 2 桁にそろえて取り出す。`formatToParts` で組み立てるのは、`toLocaleString`
 * の区切りや並びがブラウザ・言語ごとに違い、表の列で桁がそろわないため。年・月・日・時・分は
 * 指定したので必ず入る。
 * @graph-connects none
 */
function localParts(iso: string, timeZone: string | undefined): Record<string, string> | null {
  const date = new Date(iso);
  if (iso === '' || Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

/**
 * ISO 8601 の日時を、見ている人の時刻帯の日付（YYYY-MM-DD）にする。読めなければ空文字。
 *
 * UTC の文字列の先頭 10 文字を切ると、日本時間の朝 9 時より前に保存したものが前日になる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ISO 8601 の日時を閲覧者の時刻帯の日付（YYYY-MM-DD）に整形する
 * @graph-connects localParts [calls] 日時の要素の取り出し
 */
export function formatLocalYmd(iso: string, timeZone?: string): string {
  const p = localParts(iso, timeZone);
  return p === null ? '' : `${p.year}-${p.month}-${p.day}`;
}

/**
 * ISO 8601 の日時を、見ている人の時刻帯の日時（YYYY-MM-DD HH:mm）にする。読めなければ空文字。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ISO 8601 の日時を閲覧者の時刻帯の日時（YYYY-MM-DD HH:mm）に整形する
 * @graph-connects localParts [calls] 日時の要素の取り出し
 */
export function formatLocalYmdHm(iso: string, timeZone?: string): string {
  const p = localParts(iso, timeZone);
  return p === null ? '' : `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}
